import React, { createContext, useEffect, useState } from "react";
import { Evaluation } from "@/services/evaluationReports.service";
import {
  ContainsOperator,
  EqualsOperator,
  EvaluatorReturnTypeToFilterType,
  Filter,
  FilterTarget,
  FilterType,
  GreaterThanOperator,
  HasErrorOperator,
  LessThanOperator,
  NestedTarget,
  NoErrorOperator,
  NotContainsOperator,
  NotEqualsOperator,
  Operator,
} from "@/lib/filtering/filters";
import { useQueryState } from "nuqs";
import { parseAsArrayOf } from "nuqs";
import {
  getDatapointInputsFilterTarget,
  getDatapointSingleInputFilterTarget,
  getEvaluatorFilterTarget,
  getJudgmentFilterTarget,
} from "@/lib/filtering/create";
import { Evaluator } from "@/types/app/evaluator";
interface FilterContextInterface {
  inputs: Set<string>;
  setInputs?: (inputs: Set<string>) => void;
  filters: Filter[];
  filterTargets: FilterTarget[];
  setFilters: (filters: Filter[]) => void;
}

export const serializeDatapointFilter = (filter: Filter): string => {
  if (filter.value === null) {
    return "";
  }
  const prefix = ["ne", "nc", "ok"].includes(filter.operator.code) ? "-" : "";
  let operatorSymbol: string;
  switch (filter.operator.code) {
    case "gt":
      operatorSymbol = ">";
      break;
    case "lt":
      operatorSymbol = "<";
      break;
    case "eq":
    case "ne":
      operatorSymbol = ":";
      break;
    case "co":
    case "nc":
      operatorSymbol = "~";
      break;
    case "er":
    case "ok":
      operatorSymbol = "!";
      break;
    default:
      operatorSymbol = "";
      break;
  }

  if (filter.target.entity === "inputs") {
    const property = filter.target.label;
    const unquotedStr = `${prefix}datapoint.inputs.${filter.target.label}${operatorSymbol}${filter.value}`;

    return encodeURIComponent(unquotedStr);
  } else if (filter.target.entity === "evaluator") {
    const version_id = (filter.target.target as Evaluator).version_id;
    const unquotedStr = `${prefix}evaluator.${version_id}.judgment${operatorSymbol}${filter.value}`;

    return encodeURIComponent(unquotedStr);
  } else {
    throw new Error("Invalid filter");
  }
};

const FilterContext = createContext<FilterContextInterface | undefined>(undefined);

export const useFilterContext = () => {
  const context = React.useContext(FilterContext);
  if (context === undefined) {
    throw new Error("useFilterContext must be used within a FilterContextProvider");
  }
  return context;
};

const parseAsEvaluationFilter = (queryValue: string, evaluators: Evaluator[], inputs: Set<string>): Filter | null => {
  // Decode the URL-encoded string first
  const decoded = decodeURIComponent(queryValue);
  const unquoted = decoded.replace(/^"(.*)"$/, "$1").replace(/\\"/g, '"');
  const datapointFilterMatch = unquoted.match(/^(-)?datapoint\.inputs\.([^:~]+)([:~])(.+)$/);
  const evaluatorFilterMatch = unquoted.match(/^(-)?evaluator\.(evv_[^.]+)\.judgment([:~<>!])(.+)$/);
  if (datapointFilterMatch) {
    const [_, isNegative, variable, operator, value] = datapointFilterMatch;

    let filterOperator: Operator;
    if (operator === ":") {
      filterOperator = isNegative ? NotEqualsOperator : EqualsOperator;
    } else {
      filterOperator = isNegative ? NotContainsOperator : ContainsOperator;
    }

    const target = getDatapointSingleInputFilterTarget(variable);
    const parentTarget = getDatapointInputsFilterTarget(Array.from(inputs));
    return {
      target: target,
      operator: filterOperator,
      value: value,
      type: "text",
    };
  } else if (evaluatorFilterMatch) {
    const [_, isNegative, evaluatorVersionId, operator, value] = evaluatorFilterMatch;
    let filterOperator: Operator = EqualsOperator;
    if (operator === ":") {
      filterOperator = isNegative ? NotEqualsOperator : EqualsOperator;
    } else if (operator === "~") {
      filterOperator = isNegative ? NotContainsOperator : ContainsOperator;
    } else if (operator === "<") {
      filterOperator = LessThanOperator;
    } else if (operator === ">") {
      filterOperator = GreaterThanOperator;
    } else if (operator === "!") {
      filterOperator = isNegative ? NoErrorOperator : HasErrorOperator;
    }

    const evaluator = evaluators.find((e) => e.version_id === evaluatorVersionId);
    if (!evaluator) {
      return null;
    }

    const target = getEvaluatorFilterTarget(evaluator, evaluators);
    const parentTarget = getJudgmentFilterTarget(evaluators);

    return {
      target: target,
      replacementTargets: parentTarget.childTargets,
      operator: filterOperator,
      value: value,
      type: EvaluatorReturnTypeToFilterType(evaluator.spec.return_type),
    };
  }

  return null;
};

const createEvaluationFilterParser = (evaluators: Evaluator[], inputs: Set<string>) => {
  return {
    parse: (value: string) => parseAsEvaluationFilter(value, evaluators, inputs),
    serialize: serializeDatapointFilter,
  };
};

export const FilterContextProvider = ({
  children,
  evaluation,
}: React.PropsWithChildren<{ evaluation: Evaluation | undefined }>) => {
  const [inputs, setInputs] = React.useState<Set<string>>(new Set());
  const [filters, setFilters] = useQueryState(
    "filters",
    parseAsArrayOf(
      createEvaluationFilterParser(evaluation?.evaluators.map((e) => e.version) || [], inputs),
    ).withDefault([]),
  );

  useEffect(() => {
    // We use this useEffect to update inputs filter with the latest inputs when they loaded from datapoints
    setFilters(
      filters.map((filter) => {
        if (filter.target.entity === "inputs") {
          return {
            ...filter,
            replacementTargets: Array.from(inputs).map((input) => getDatapointSingleInputFilterTarget(input)),
          };
        }

        return filter;
      }),
    );
    // Do not add filter and setFilter to the array of dependencies, as it will cause infinite re-renders
  }, [inputs, setInputs]);

  const inputFilterTarget = inputs.size > 0 ? getDatapointInputsFilterTarget(Array.from(inputs)) : undefined;
  const evaluatorFilterTarget =
    evaluation && evaluation.evaluators.length > 0
      ? getJudgmentFilterTarget(evaluation?.evaluators.map((e) => e.version) ?? [])
      : undefined;

  const filterTargets = [inputFilterTarget, evaluatorFilterTarget].filter(
    (filter) => filter !== undefined,
  ) as FilterTarget[];

  return (
    <FilterContext.Provider value={{ filters, filterTargets, setFilters, inputs, setInputs }}>
      {children}
    </FilterContext.Provider>
  );
};
